/**
 * Widget'ın varsayılan stilleri.
 *
 * **Neden JS içinde string?** `<head>`'in **en başına** enjekte edebilmek için.
 * Cascade'de ilk sırada olduğumuz için mağazanın yazdığı her kural — hangi
 * dosyada olursa olsun — eşit özgüllükte bizi ezer. `!important` savaşına
 * girmeye gerek kalmaz. Ekstra bir HTTP isteği de olmaz.
 *
 * `styles: false` verilirse hiç enjekte edilmez; mağaza sıfırdan kendi CSS'ini
 * yazar. Class isimleri **public sözleşmedir** — değiştirmek yeni major sürüm
 * gerektirir (bkz. widget/README.md).
 *
 * Özgüllük bilinçli olarak düşük tutuldu: tek class seçicileri, `!important` yok,
 * id yok. Renkler CSS değişkenlerinden gelir, mağaza tek satırla temayı çevirir.
 */

export const WIDGET_STYLE_ID = "shs-styles";

export const styles = `
/* Tema değişkenleri hem modalda hem tetikleyicide geçerli olsun diye
   ikisinde de tanımlı. Mağaza tek satırla temayı çevirir:
   .shs-trigger,.shs-dialog{--shs-accent:#e11d48} */
.shs-dialog,.shs-trigger{
  --shs-bg:#fff; --shs-fg:#111827; --shs-muted:#6b7280; --shs-border:#e5e7eb;
  --shs-accent:#0055ff; --shs-accent-ring:rgba(0,85,255,.22);
  --shs-hover:#f3f4f6; --shs-radius:12px; --shs-max-width:1100px;
  /* Varsayilan: magazanin fontunu miras al (widget siteye ait gorunsun).
     Zorlamak icin: .shs-dialog,.shs-trigger{--shs-font:system-ui,sans-serif} */
  --shs-font:inherit;
  padding:0; border:0; background:transparent; max-width:100vw; max-height:100vh;
  width:100%; color:var(--shs-fg);
  font-family:var(--shs-font); font-size:16px; line-height:1.5;
}
.shs-dialog::backdrop{background:rgba(15,23,42,.55)}

/* ── Sertleştirme katmanı ───────────────────────────────────────────────
   Mağaza temaları sık sık input {...!important} / img {...!important} gibi
   genel kurallar yazıyor; bunlar modalın içine sızıp görünümü bozuyor
   (kırmızı kesikli çerçeve, 14px padding vb.).

   Çözüm enjeksiyon sırası DEĞİL, özgüllük: iki !important bildirimi
   çarpıştığında cascade önce özgüllüğe bakar. Mağazanın input seçicisi
   (0,0,1), bizim .shs-dialog .shs-input (0,2,0) → biz kazanırız.

   Yalnızca **yapısal** özellikler sertleştirildi. Renk, font ve radius
   değişkenlerden geldiği için mağaza temayı hâlâ tek satırla çevirebilir. */
.shs-dialog .shs-input{
  border:0 !important; outline:0 !important; margin:0 !important;
  padding:6px 0 !important; background:transparent !important;
  box-shadow:none !important; border-radius:0 !important;
  height:auto !important; min-height:0 !important; max-width:none !important;
  font-size:16px !important; line-height:1.5 !important;
  text-transform:none !important; letter-spacing:normal !important;
}
.shs-dialog .shs-option__thumb{
  width:36px !important; height:36px !important; max-width:36px !important;
  margin:0 !important; padding:0 !important; display:block !important;
  flex:0 0 auto; object-fit:contain; border:1px solid var(--shs-border);
  border-radius:6px; background:#fff;
}
.shs-dialog .shs-item__image{
  width:48px !important; height:48px !important; max-width:48px !important;
  margin:0 !important; padding:0 !important; display:block !important;
}
.shs-dialog[data-fallback]{
  position:fixed; inset:0; z-index:2147483000; display:none;
  background:rgba(15,23,42,.55);
}
.shs-dialog[data-fallback][open]{display:block}

.shs-panel{
  box-sizing:border-box; width:100%; max-width:var(--shs-max-width);
  margin:8vh auto 0; background:var(--shs-bg);
  border:1px solid var(--shs-border); border-radius:var(--shs-radius);
  box-shadow:0 20px 50px -12px rgba(0,0,0,.35);
  display:flex; flex-direction:column; overflow:hidden;
  max-height:min(80vh,720px);
}
.shs-panel *{box-sizing:border-box}

.shs-header{display:flex; align-items:center; gap:10px; padding:12px 14px; border-bottom:1px solid var(--shs-border)}
.shs-header svg{flex:0 0 auto; color:var(--shs-muted)}
/* font-size 16px altına düşürmeyin: iOS Safari odakta sayfayı zoom'lar. */
.shs-input{
  flex:1 1 auto; min-width:0; border:0; outline:0; background:transparent;
  font:inherit; font-size:16px; color:var(--shs-fg); padding:6px 0;
  caret-color:var(--shs-accent); box-shadow:none; border-radius:0;
  -webkit-appearance:none; appearance:none;
}
.shs-input::placeholder{color:var(--shs-muted); opacity:1}
/* Safari'nin search input'a eklediği temizleme/büyüteç süslerini kaldır. */
.shs-input::-webkit-search-decoration,
.shs-input::-webkit-search-cancel-button{-webkit-appearance:none; appearance:none}

.shs-btn{
  flex:0 0 auto; display:inline-flex; align-items:center; gap:4px;
  border:1px solid var(--shs-border); border-bottom-width:2px; border-radius:6px;
  background:var(--shs-bg); color:var(--shs-muted); font:inherit; font-size:11px;
  line-height:1; padding:5px 7px; cursor:pointer;
}
.shs-btn:hover{background:var(--shs-hover); color:var(--shs-fg)}
.shs-btn:focus-visible{outline:none; border-color:var(--shs-accent); box-shadow:0 0 0 3px var(--shs-accent-ring)}
/* Geniş ekranda "esc", dokunmatik/dar ekranda × göster. */
.shs-btn__esc{display:inline}
.shs-btn__x{display:none; font-size:15px}
@media (max-width:640px){
  .shs-btn__esc{display:none}
  .shs-btn__x{display:inline}
}

.shs-body{overflow-y:auto; -webkit-overflow-scrolling:touch}

/* V1 düzeni: solda öneriler ve gruplar, sağda ürünler. Dar ekranda tek kolon. */
.shs-columns{display:grid; grid-template-columns:minmax(0,400px) minmax(0,1fr)}
.shs-columns[hidden]{display:none}
.shs-side{padding:12px 10px; border-right:1px solid var(--shs-border); display:flex; flex-direction:column; gap:2px}
.shs-side[hidden]{display:none}
.shs-side[hidden]+.shs-main{grid-column:1 / -1}
.shs-main{padding:12px 10px; min-width:0}
.shs-main__empty{list-style:none; padding:28px 16px; text-align:center; color:var(--shs-fg); font-size:14px}
.shs-main__empty p{margin:0}
.shs-main__empty .shs-main__empty-hint{margin-top:6px; color:var(--shs-muted); font-size:13px}
.shs-section__title{
  padding:10px 8px 6px; font-size:11px; font-weight:600; letter-spacing:.06em;
  text-transform:uppercase; color:var(--shs-muted);
}
.shs-side .shs-section__title:first-child,.shs-main__title{padding-top:2px}
.shs-option{
  display:flex; align-items:center; gap:10px; padding:6px 8px; border-radius:8px;
  font-size:14px; cursor:pointer;
}
.shs-option:hover,.shs-option.shs-item--active{background:var(--shs-hover)}
.shs-option__label{flex:1 1 auto; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap}
.shs-option__icon{
  flex:0 0 auto; width:36px; height:36px; border-radius:6px; background:var(--shs-hover);
  display:inline-flex; align-items:center; justify-content:center; color:var(--shs-muted);
}
.shs-option__icon--plain{width:18px; height:18px; background:transparent}
.shs-option__meta{flex:0 0 auto; font-size:11px; color:var(--shs-muted)}
.shs-option__count{
  flex:0 0 auto; min-width:24px; text-align:center; font-size:12px;
  border:1px solid var(--shs-border); border-radius:11px; padding:0 7px;
  font-variant-numeric:tabular-nums;
}
.shs-mark{font-weight:700}
.shs-list{list-style:none; margin:0; padding:6px}
.shs-item{
  display:flex; align-items:center; gap:12px; padding:8px; border-radius:8px;
  text-decoration:none; color:inherit; cursor:pointer;
}
.shs-item:hover,.shs-item--active{background:var(--shs-hover)}
.shs-item__image{
  flex:0 0 auto; width:48px; height:48px; object-fit:contain;
  border:1px solid var(--shs-border); border-radius:6px; background:#fff;
}
.shs-item__placeholder{
  flex:0 0 auto; width:48px; height:48px; border:1px solid var(--shs-border);
  border-radius:6px; background:var(--shs-hover);
}
.shs-item__text{min-width:0; flex:1 1 auto}
.shs-item__title{display:block; font-size:14px; font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap}
.shs-item__code{display:block; font-size:12px; color:var(--shs-muted)}
.shs-item__meta{flex:0 0 auto; text-align:right}
.shs-item__price{display:block; font-size:14px; font-weight:500; font-variant-numeric:tabular-nums}
.shs-item__list-price{display:block; font-size:12px; color:var(--shs-muted); font-variant-numeric:tabular-nums}

.shs-state{padding:28px 16px; text-align:center; color:var(--shs-muted); font-size:14px}
.shs-state--error{color:#b91c1c}
.shs-state__action{margin-top:10px}

.shs-footer{
  display:flex; align-items:center; justify-content:space-between; gap:10px;
  padding:8px 12px; border-top:1px solid var(--shs-border);
  font-size:12px; color:var(--shs-muted);
}
.shs-footer__all{color:var(--shs-accent); text-decoration:none}
.shs-footer__all:hover{text-decoration:underline}
.shs-kbd{
  border:1px solid var(--shs-border); border-bottom-width:2px; border-radius:4px;
  padding:1px 5px; font-size:11px; font-family:inherit;
}

/* Mağazanın sayfasına koyduğu asıl arama çubuğu. Bir button elemanıdır — mağazanın
   input {...} kuralları buna sızmaz, sadece görsel olarak input gibi durur.
   (Bu blok bir template literal içinde: yorumlara backtick YAZMAYIN, literal kapanır.) */
.shs-trigger{
  display:inline-flex; align-items:center; gap:10px; width:100%;
  box-sizing:border-box; min-height:44px; padding:0 12px;
  border:1px solid var(--shs-border); border-radius:10px;
  background:var(--shs-bg); color:var(--shs-muted);
  font:inherit; font-family:var(--shs-font); font-size:14px; line-height:1.2; text-align:left;
  cursor:pointer; -webkit-appearance:none; appearance:none;
  transition:border-color .15s ease, box-shadow .15s ease, background-color .15s ease;
}
.shs-trigger:hover{border-color:var(--shs-muted); background:var(--shs-hover)}
.shs-trigger:focus-visible{
  outline:none; border-color:var(--shs-accent);
  box-shadow:0 0 0 3px var(--shs-accent-ring);
}
.shs-trigger svg{color:var(--shs-muted)}
.shs-trigger__label{
  flex:1 1 auto; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}
/* Klavye kısayolu ipucu — yalnızca hotkey açıkken ve dar olmayan ekranda. */
.shs-trigger__kbd{
  display:none; flex:0 0 auto;
  border:1px solid var(--shs-border); border-bottom-width:2px; border-radius:5px;
  padding:2px 5px; font-size:11px; line-height:1.4; color:var(--shs-muted);
}
.shs-trigger[data-hotkey="1"] .shs-trigger__kbd{display:inline-block}
@media (max-width:640px){
  .shs-trigger[data-hotkey="1"] .shs-trigger__kbd{display:none}
}

@media (max-width:760px){
  .shs-columns{grid-template-columns:minmax(0,1fr)}
  .shs-side{border-right:0; border-bottom:1px solid var(--shs-border)}
}
@media (max-width:640px){
  .shs-panel{
    margin:0; max-width:none; min-height:100vh; max-height:100vh;
    border:0; border-radius:0;
    padding-bottom:env(safe-area-inset-bottom);
  }
}
/* Koyu tema: "dark" her zaman, "auto" yalnız ziyaretçinin sistemi koyuysa.
   İki blok aynı değerleri taşır; değiştirirken ikisini birlikte güncelleyin. */
.shs-dialog[data-theme="dark"],.shs-trigger[data-theme="dark"]{
  --shs-bg:#111827; --shs-fg:#f9fafb; --shs-muted:#9ca3af;
  --shs-border:rgba(255,255,255,.14); --shs-hover:rgba(255,255,255,.07);
}
.shs-trigger[data-theme="dark"]{color:var(--shs-muted)}
.shs-dialog[data-theme="dark"] .shs-item__image{background:#fff}
@media (prefers-color-scheme:dark){
  .shs-dialog[data-theme="auto"],.shs-trigger[data-theme="auto"]{
    --shs-bg:#111827; --shs-fg:#f9fafb; --shs-muted:#9ca3af;
    --shs-border:rgba(255,255,255,.14); --shs-hover:rgba(255,255,255,.07);
  }
  .shs-trigger[data-theme="auto"]{color:var(--shs-muted)}
  .shs-dialog[data-theme="auto"] .shs-item__image{background:#fff}
}
@media (prefers-reduced-motion:reduce){
  .shs-dialog,.shs-panel{transition:none}
}
`;

/**
 * Stilleri `<head>`'in **en başına** ekler (mağazanın kuralları sonra gelip
 * bizi ezebilsin). Aynı sayfada iki kez çağrılırsa ikinci kez hiçbir şey yapmaz.
 */
export const injectStyles = (doc: Document = document) => {
  if (doc.getElementById(WIDGET_STYLE_ID)) return;
  const el = doc.createElement("style");
  el.id = WIDGET_STYLE_ID;
  el.textContent = styles;
  const head = doc.head || doc.documentElement;
  head.insertBefore(el, head.firstChild);
};
